import { FC } from "react";
import withPage from "../../components/Page";
import Section from "../../components/Section";
import Hero from "../../components/Hero";
import Button from "../../components/Button";
import CtaSection from "../../components/CtaSection";
import { globalContent } from "../../config/content";
import styles from "./RequestDemo.module.scss";

const RequestDemo: FC = () => {
  const emailAddress = globalContent.contact.email;
  const mailtoSubject = "Pulse demo request";
  const mailtoBody = `Hello Pulse team,

I would like to request a demo of Pulse.

My details:
Name: [Your Name]
Organisation: [Your Organisation]

What I would like to see:
[e.g. Building and O&M information, documents and drawings, asset information, search, etc.]`;

  const mailtoLink = `mailto:${emailAddress}?subject=${encodeURIComponent(
    mailtoSubject,
  )}&body=${encodeURIComponent(mailtoBody)}`;

  return (
    <div className={styles.requestDemoPage}>
      {/* 1. Hero */}
      <Hero
        eyebrow="Request a Demo"
        heading="See Pulse in action."
        copy="See how Pulse can bring your building information, O&M documentation, drawings and asset information together in one secure, structured platform."
        backgroundImage={require("../../assets/images/hero/request-demo.webp")}
        button={{
          text: "Request a Demo",
          href: mailtoLink,
          variant: "secondary",
        }}
        secondaryButton={{
          text: "Explore the Demo",
          to: globalContent.demoUrl,
          variant: "outlineLight",
        }}
      />

      {/* 2. How the Demo Works */}
      <Section bgColor="off-white">
        <div className={styles.sectionContainer}>
          <div className={styles.sectionHeader}>
            <span className={styles.eyebrow}>Demonstration</span>
            <h2 className={styles.sectionHeading}>
              A practical look at Pulse.
            </h2>
            <p className={styles.sectionCopy}>
              Every building is different. Our demonstrations are tailored to
              give you a practical look at how Pulse can support your team and
              your portfolio.
            </p>
          </div>

          <div className={styles.focusListCard}>
            <h3 className={styles.cardTitle}>Focus areas can include:</h3>
            <ul className={styles.focusList}>
              <li>
                <span className={styles.bulletIcon}>✓</span>
                <div>
                  <strong>Building and O&M information:</strong> Turn manuals
                  and documentation into a structured digital resource.
                </div>
              </li>
              <li>
                <span className={styles.bulletIcon}>✓</span>
                <div>
                  <strong>Documents and drawings:</strong> Access as-built
                  drawings and technical records instantly.
                </div>
              </li>
              <li>
                <span className={styles.bulletIcon}>✓</span>
                <div>
                  <strong>Asset information:</strong> Connect equipment records
                  with maintenance history and documentation.
                </div>
              </li>
              <li>
                <span className={styles.bulletIcon}>✓</span>
                <div>
                  <strong>Search and discovery:</strong> Find specific
                  information in seconds rather than searching through folders.
                </div>
              </li>
              <li>
                <span className={styles.bulletIcon}>✓</span>
                <div>
                  <strong>Workflow integration:</strong> See how Pulse fits into
                  your existing property and facilities management workflows.
                </div>
              </li>
            </ul>
            <p className={styles.cardNote}>
              Demonstrations are conducted online and typically take 30 minutes,
              with plenty of time for questions.
            </p>
          </div>
        </div>
      </Section>

      {/* 3. What to Include in Your Email */}
      <Section bgColor="white">
        <div className={styles.sectionContainer}>
          <div className={styles.splitLayout}>
            <div className={styles.splitContent}>
              <span className={styles.eyebrow}>No Long Forms</span>
              <h2 className={styles.sectionHeading}>Just a few details.</h2>
              <p className={styles.sectionCopy}>
                We believe requesting a demo should be straightforward. When you
                click request a demo, your email client will open with a short,
                pre-populated template.
              </p>
              <p className={styles.sectionCopy}>
                You don't need to fill out lengthy forms, provide phone numbers,
                or enter detailed building specifications before speaking with
                us.
              </p>
              <div className={styles.actionContainer}>
                <Button href={mailtoLink} variant="primary" size="lg">
                  Request a Demo
                </Button>
              </div>
            </div>

            <div className={styles.templateCard}>
              <div className={styles.templateHeader}>
                <span className={styles.dot}></span>
                <span className={styles.dot}></span>
                <span className={styles.dot}></span>
                <span className={styles.templateTitle}>Email</span>
              </div>
              <div className={styles.templateBody}>
                <div className={styles.templateField}>
                  <span className={styles.label}>To:</span> {emailAddress}
                </div>
                <div className={styles.templateField}>
                  <span className={styles.label}>Subject:</span> Pulse demo
                  request
                </div>
                <div className={styles.templateContent}>
                  <p>Hello Pulse team,</p>
                  <p>I would like to request a demo of Pulse.</p>
                  <p>
                    <strong>My details:</strong>
                    <br />
                    Name: [Your Name]
                    <br />
                    Organisation: [Your Organisation]
                  </p>
                  <p>
                    <strong>What I would like to see:</strong>
                    <br />
                    [e.g. Building and O&M information, drawings, asset data,
                    etc.]
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* 4. Final CTA */}
      <CtaSection
        eyebrow="Ready to see Pulse?"
        heading="Ready to see Pulse?"
        copy="Request a demonstration and see how Pulse can make building information easier to find, understand and use."
        button={{
          text: "Request a Demo",
          href: mailtoLink,
          variant: "secondary",
        }}
      />
    </div>
  );
};

export default withPage(RequestDemo)({
  meta: globalContent.seo.requestDemo,
});
